import { ApplicationRef, DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, interval } from 'rxjs';
import { NoticeDialogService } from './notice-dialog.service';

@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {
  private readonly appRef = inject(ApplicationRef);
  private readonly updates = inject(SwUpdate);
  private readonly noticeDialog = inject(NoticeDialogService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly updateCheckIntervalMs = 6 * 60 * 60 * 1000;
  private initialized = false;
  private promptOpen = false;

  init(): void {
    if (this.initialized || !this.updates.isEnabled) return;

    this.initialized = true;
    this.listenForUpdates();
    this.scheduleUpdateChecks();
  }

  checkForUpdate(): void {
    if (!this.updates.isEnabled) return;

    this.updates.checkForUpdate().catch(() => undefined);
  }

  promptForUpdate(): void {
    if (this.promptOpen) return;

    this.promptOpen = true;
    this.noticeDialog.showConfirmation('common.modal.title.update', 'common.modal.message.updateAvailable', () => {
      this.activateUpdateAndReload();
    });
  }

  private listenForUpdates(): void {
    this.updates.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.promptForUpdate());

    this.updates.unrecoverable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.promptForUpdate();
    });
  }

  private scheduleUpdateChecks(): void {
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable));
    const everySixHours$ = interval(this.updateCheckIntervalMs);

    concat(appIsStable$, everySixHours$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkForUpdate());
  }

  private activateUpdateAndReload(): void {
    this.updates
      .activateUpdate()
      .then(() => document.location.reload())
      .catch(() => {
        this.promptOpen = false;
      });
  }
}
